'use client';
import React from 'react';
import { api } from '@/trpc/react';
import { match } from 'ts-pattern';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { convertToReadableText } from '@acme/lib';
import { Check } from 'lucide-react';
import { Control, useController } from 'react-hook-form';

// test
interface WildcardCategoryStepProps {
  category: string;
  onSelect: (wildcardModifierKey: string) => void;
  gender?: 'male' | 'female';
  selectedValue?: string;
  control: Control<any>;
}

export function WildcardCategoryStep({ category, onSelect, gender, control }: WildcardCategoryStepProps) {
  const wildcardValuesQuery = api.theme.getModifierTagNames.useQuery(
    { category, gender: gender ? [gender] : undefined },
    { enabled: true }
  );

  const { field } = useController({
    name: 'selectedModifiers',
    control
  });

  console.log(field.value);
  // Check if the current value is "Random" for this category
  const isRandom = field.value?.[category]?.random?.value === 'Random';

  // When the category is ex "upperClothing", and we have a subcategory "casualUpperClothing", we want to check if the current value is "Random" for the subcategory
  // So we need to go through the modifiers and check if the current value is "Random" for any subcategory
  const isRandomASubcategory = Object.keys(field.value || {}).some(
    key => key !== category && key.toLowerCase().includes(category.toLowerCase()) && field.value?.[key] === 'Random'
  );

  console.log({ isRandomASubcategory: isRandomASubcategory });

  const handleRandomClick = () => {
    const currentModifiers = { ...field.value };

    // Remove any existing values for this category
    Object.keys(currentModifiers).forEach(key => {
      if (key === category || key.toLowerCase().includes(category.toLowerCase())) {
        delete currentModifiers[key];
      }
    });

    // Set the Random value for this category using the new structure
    currentModifiers[category] = {
      random: { value: 'Random' }
    };

    // Update the form
    field.onChange(currentModifiers);
  };

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-2xl font-bold mb-4">Select {convertToReadableText(category)} Category</h2>
      {match(wildcardValuesQuery)
        .with({ status: 'pending' }, () => (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ))
        .with({ status: 'error' }, () => <div>Error loading categories</div>)
        .with({ status: 'success' }, ({ data }) => {
          const subcategories = data;

          return (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-2">
                {/* Random button with check or chevron */}
                <Button variant="outline" className="w-full flex justify-between" onClick={handleRandomClick}>
                  <span>Random</span>
                  {isRandom && !isRandomASubcategory ? (
                    <Check className="h-4 w-4 text-accent" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </Button>

                {subcategories.map(subcategory => (
                  <Button
                    key={subcategory}
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => onSelect(subcategory)}
                  >
                    {convertToReadableText(subcategory)}
                  </Button>
                ))}
              </div>
            </ScrollArea>
          );
        })
        .otherwise(() => null)}
    </div>
  );
}
